import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import { GenericBackgroundImage } from 'takeovers/utils/components/GenericBackgroundImage'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

import Image from 'components/Common/Image'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { getThemeClassName } from 'lib/theme/themeUtils'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Banner = styled(Group)`
  padding: ${rem(12)} ${rem(20)};
  position: relative;
  width: 100%;

  ${mediaQueryUp.desktop} {
    padding: ${rem(20)} ${rem(150)};
  }
`

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled(Group)`
  position: relative;
  z-index: 1;
`

const CTAButtonWrapper = styled.div`
  /* Reset to default theme for white button background */
`

const DesktopBreakpoint = styled(CSSBreakpoint)`
  flex: 1;
  width: 100%;
`

interface Props {
  vertical?: App.SiteTakeoverVertical
}

function SiteTakeoverSearchBanner({ vertical }: Props) {
  const siteTakeoverTheme = useSiteTakeoverTheme()
  const customTakeover = useCurrentSiteTakeoverV2({ vertical })

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const useLab = !!siteTakeoverTheme && !!siteTakeoverBannerContentPayload
  const useCustom = !useLab && !!customTakeover?.searchPage?.bannerContent

  if (!useLab && !useCustom) {
    return null
  }

  const shouldHideOnMobile = useCustom && customTakeover?.searchPage?.hideOnMobileScreenSize

  // Extract values for lab config
  const logoRef = siteTakeoverTheme?.logoRef
  const title = siteTakeoverBannerContentPayload?.title
  const subtitle = siteTakeoverBannerContentPayload?.subtitle
  const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
  const backgroundRef = siteTakeoverTheme?.backgroundRef
  const backgroundStyle = siteTakeoverTheme?.backgroundStyle
  const paletteType = useLab ? (siteTakeoverTheme?.palette ?? 'inverse') : customTakeover?.palette

  const takeOverBackground = backgroundRef ? <GenericBackgroundImage imageId={backgroundRef} backgroundStyle={backgroundStyle} /> : undefined

  // Determine banner content based on config type
  const bannerContent = useLab ? (
    <>
      <CSSBreakpoint max="tablet">
        <Group direction="vertical" gap={4} verticalAlign="start">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {logoRef && <Image id={logoRef} dpr={2} width={32} height={32} />}
            <Heading variant="heading4">{title}</Heading>
          </Group>
          <BodyText variant="medium">
            {subtitle}
            {ctaLink && (
              <>
                <span style={{ marginRight: rem(8) }} />
                <TextLink to={ctaLink} weight="bold">View all</TextLink>
              </>
            )}
          </BodyText>
        </Group>
      </CSSBreakpoint>

      <DesktopBreakpoint min="desktop">
        <Group direction="horizontal" gap={16} verticalAlign="center" fullWidth>
          {logoRef && <Image id={logoRef} dpr={2} width={60} height={60} />}
          <Group direction="vertical" gap={4} verticalAlign="start" fullWidth grow>
            <Heading variant="heading3">{title}</Heading>
            <BodyText variant="large">{subtitle}</BodyText>
          </Group>
          {ctaLink && (
            <CTAButtonWrapper className={getThemeClassName({ mobile: 'inverse' })}>
              <TextButton kind="primary" to={ctaLink}>
                View all offers
              </TextButton>
            </CTAButtonWrapper>
          )}
        </Group>
      </DesktopBreakpoint>
    </>
  ) : customTakeover?.searchPage?.bannerContent

  return (
    <CSSBreakpoint min={shouldHideOnMobile ? 'tablet' : undefined}>
      <Banner direction="horizontal" verticalAlign="center" className={getThemeClassName({ mobile: paletteType })}>
        {useCustom && <Background background="default" vertical={vertical} />}
        {(useLab && takeOverBackground) && <SiteTakeoverBackground>{takeOverBackground}</SiteTakeoverBackground>}
        <Content direction="horizontal" gap={12} verticalAlign="center" fullWidth>
          {bannerContent}
        </Content>
      </Banner>
    </CSSBreakpoint>
  )
}

export default SiteTakeoverSearchBanner
